import type { AssistantActivity, CharacterKey } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { AiConsentSheet, hasAiConsent } from '../components/assistant/AiConsentSheet.js';
import { ResultCards } from '../components/assistant/ResultCards.js';
import { CharacterStage } from '../components/stage/CharacterStage.js';
import { paletteFor } from '../components/voice/characters3d.js';
import { streamAssistantMessage } from '../lib/api/assistant.js';
import {
  characterName,
  characterTile,
  DEFAULT_CHARACTER,
  type Expression,
  FAMILIES,
} from '../lib/characters.js';
import {
  canDictate,
  type Dictation,
  hapticSuccess,
  hapticTap,
  startDictation,
} from '../lib/native.js';
import { DESK_PREF, deskAvailable } from '../lib/deskMode.js';
import { inboxSupported, setInboxDraftWanted } from '../lib/inbox.js';
import { liveServiceAvailable, openAssistantSettings } from '../lib/liveService.js';
import { notifyPermission, requestNotifyPermission } from '../lib/notify.js';
import { enablePreview, setPreviewWanted } from '../lib/pushPreview.js';
import { InboxConsent } from '../components/conta/InboxConsent.js';
import { track } from '../lib/track.js';
import type { LiveMood } from '../lib/voice/controller.js';
import { resetPose, setPose } from '../lib/voice/pose.js';
import { PROFILE_KEY, useProfile, useUpdateProfile } from '../lib/useProfile.js';
import './boas-vindas.css';

type Scene = 'oi' | 'pedido' | 'feito' | 'pronto' | 'rosto';
const KEY = 'secretario.boas-vindas';
/** O que vem marcado em "Vamos deixar tudo pronto" (a pessoa pode desmarcar). */
const READY_ITEMS = [
  { key: 'notify', title: 'Notificações', hint: 'Te aviso na hora, mesmo com o celular parado' },
  { key: 'preview', title: 'Texto na notificação', hint: 'Você lê a mensagem sem abrir o app' },
  { key: 'web', title: 'Pesquisar na internet', hint: 'Busco o que é atual e mostro as fontes' },
  {
    key: 'desk',
    title: 'Deixar na mesa',
    hint: 'Com o app aberto, diga "Ô, {nome}". O celular ouve só o meu nome, nele mesmo.',
  },
] as const;
/** Pedidos completos (o quê, o dia e a hora): o primeiro valor sai sem pergunta de volta. */
const SUGGESTIONS = [
  'Me lembra da luz dia 10 às 9h',
  'Remédio amanhã às 8h',
  'Dentista quinta às 15h',
];

/**
 * A escolha do rosto, na ordem pedida em 10/10/2026: os dois a traço (o Caio é o de quem
 * chega), a Gota, o Robô e, por último, um Face só (o amarelo). A cor do Face e os
 * demais personagens ficam em Mudar jeito, depois do cadastro.
 */
const PICK_FAMILIES = ['traco-ela', 'traco-ele', 'gota', 'robo-s', 'face-sol'];
const FACES: CharacterKey[] = PICK_FAMILIES.flatMap((id) => {
  const family = FAMILIES.find((item) => item.id === id);
  const key = family ? Object.values(family.variants)[0]?.key : undefined;
  return key ? [key] : [];
});

/**
 * O personagem vivo num retrato (pedido, feito e escolha): pisca, olha, pensa com a pose
 * sorteada enquanto resolve o primeiro pedido e "fala" a resposta.
 */
function Portrait({
  characterKey,
  name,
  mood,
  expression = 'simpatico',
  say,
  size = 'md',
}: {
  characterKey: CharacterKey;
  name: string;
  mood: LiveMood;
  expression?: Expression;
  say?: string;
  size?: 'sm' | 'md';
}) {
  const palette = paletteFor(characterKey);
  return (
    <div
      className={`bv-portrait bv-portrait-${size}`}
      style={
        {
          '--bv-light': palette.light,
          '--bv-mid': palette.mid,
          '--bv-deep': palette.deep,
        } as CSSProperties
      }
    >
      <CharacterStage
        key={characterKey}
        characterKey={characterKey}
        expression={expression}
        mood={mood}
        name={name}
        {...(say ? { say } : {})}
      />
    </div>
  );
}

function readScene(): Scene {
  try {
    const saved = window.localStorage.getItem(KEY);
    // Retomada: volta para a cena em que parou, menos a de confirmação (o cartão não fica salvo).
    if (saved === 'pedido' || saved === 'pronto' || saved === 'rosto') return saved;
  } catch {
    /* sem armazenamento: começa do início */
  }
  return 'oi';
}

function saveScene(scene: Scene | null): void {
  try {
    if (scene) window.localStorage.setItem(KEY, scene);
    else window.localStorage.removeItem(KEY);
  } catch {
    /* segue sem lembrar */
  }
}

/** O cartão que mostra o que ele fez: o que tem Desfazer primeiro, depois qualquer cartão. */
function cardOfTurn(activities: AssistantActivity[]): AssistantActivity | null {
  const done = activities.filter((item) => item.state === 'done' && item.card);
  return done.find((item) => item.card?.undo) ?? done[0] ?? null;
}

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
    <path
      d="M5.5 11a6.5 6.5 0 0 0 13 0M12 18v3"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M5 12h13M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/**
 * Apresentação para quem chega (estudo "Telas de destaque", aprovado em 05/10/2026):
 * Oi, primeiro pedido de verdade, Feito e Meu rosto. Primeiro valor em até 60 s;
 * a escolha do rosto vem depois. Sempre dá para pular; fechou no meio, volta de onde parou.
 */
export function BoasVindasPage() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const reduced = useReducedMotion();
  const profile = profileQ.data;

  const [scene, setSceneState] = useState<Scene>(readScene);
  const [characterKey, setCharacterKey] = useState<CharacterKey>(
    (profile?.characterKey as CharacterKey | undefined) ?? DEFAULT_CHARACTER,
  );
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const shownName = nameEdited
    ? name
    : profile?.secretaryName?.trim() || characterName(characterKey);

  const setScene = (next: Scene) => {
    saveScene(next);
    setSceneState(next);
    track('feature_activated', { feature: 'boas_vindas', cena: next });
  };

  // Pedido
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<string | null>(null);
  const [done, setDone] = useState<{ item: AssistantActivity; reply: string } | null>(null);
  const dictation = useRef<Dictation | null>(null);
  const [listening, setListening] = useState(false);

  // Feito
  const [notify, setNotify] = useState<'granted' | 'ask' | 'denied'>('ask');
  useEffect(() => {
    if (scene !== 'feito') return;
    void notifyPermission().then((state) =>
      setNotify(state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'ask'),
    );
  }, [scene]);

  useEffect(() => () => dictation.current?.stop(), []);

  // Enquanto resolve o primeiro pedido, ele pensa (queixo, olhar para o alto ou dedo na
  // têmpora, sorteado); a pose solta sozinha quando a resposta chega.
  useEffect(() => {
    setPose(busy ? 'pensar' : null);
  }, [busy]);
  useEffect(() => () => resetPose(), []);

  async function finish(withFace: boolean) {
    hapticTap();
    try {
      await update.mutateAsync({
        onboarded: true,
        ...(withFace
          ? { characterKey, secretaryName: shownName.trim() || characterName(characterKey) }
          : {}),
      });
    } catch {
      /* sem rede: a próxima abertura tenta de novo */
      return;
    }
    saveScene(null);
    track('feature_activated', { feature: 'boas_vindas', cena: withFace ? 'pronto' : 'pulou' });
    navigate('/', { replace: true });
  }

  async function send(raw: string, consented = false) {
    const body = raw.trim();
    if (!body || busy) return;
    if (!consented && !hasAiConsent(profile?.preferences)) {
      setConsent(body);
      return;
    }
    hapticTap();
    setBusy(true);
    setError(null);
    setReply('');
    setSteps([]);
    let failed = false;
    let activities: AssistantActivity[] = [];
    let answer = '';
    try {
      await streamAssistantMessage(body, (event) => {
        if (event.type === 'activity') {
          setSteps((current) =>
            current.includes(event.activity.label) ? current : [...current, event.activity.label],
          );
        } else if (event.type === 'delta') {
          answer += event.text;
          setReply(answer);
        } else if (event.type === 'done') {
          activities = event.turn.activities;
          answer = event.turn.text;
          setReply(answer);
        } else if (event.type === 'error') {
          failed = true;
          setError(event.message);
        }
      });
    } catch (err) {
      failed = true;
      setError(
        (err as { message?: string } | null)?.message ||
          'Não consegui te ouvir agora. Tente de novo em instantes ou pule por enquanto.',
      );
    } finally {
      setBusy(false);
      void qc.invalidateQueries();
    }
    if (failed) return;
    const made = cardOfTurn(activities);
    if (made) {
      hapticSuccess();
      track('activated', { surface: 'boas_vindas' });
      setText('');
      setDone({ item: made, reply: answer });
      setScene('feito');
    }
    // Sem cartão (ele perguntou algo, faltou dado): a resposta fica na tela e a pessoa responde.
  }

  async function listen() {
    if (listening) {
      dictation.current?.stop();
      return;
    }
    hapticTap();
    const started = await startDictation({
      onText: (value) => setText(value),
      onEnd: () => {
        setListening(false);
        dictation.current = null;
      },
    });
    if (started) {
      dictation.current = started;
      setListening(true);
    }
  }

  // Deixe tudo pronto: o que faz o app valer desde o primeiro dia, já marcado. Cada item
  // continua sendo escolha da pessoa (pode desmarcar) e o pedido do sistema aparece por
  // cima quando precisa; nada fica ligado sem ela ver.
  const [want, setWant] = useState({ notify: true, preview: true, web: true, desk: true });
  const [readying, setReadying] = useState(false);
  const [extras, setExtras] = useState(false);
  const [inboxAsk, setInboxAsk] = useState(false);
  const desk = deskAvailable();
  const phoneExtras = liveServiceAvailable() || inboxSupported();

  /** Liga o texto na notificação quando a chave de aviso do aparelho chega (se der). */
  async function previewSoon() {
    try {
      const push = await import('../lib/push.js');
      for (let tries = 0; tries < 12; tries += 1) {
        const token = push.currentPushToken();
        if (token) {
          if (await enablePreview(token)) setPreviewWanted(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    } catch {
      /* fica para Ajustes › Notificações */
    }
  }

  async function makeReady() {
    if (readying) return;
    hapticTap();
    setReadying(true);
    try {
      await update
        .mutateAsync({
          preferences: {
            webSearch: want.web,
            notifications: {
              messages: want.notify,
              reminders: want.notify,
              secretary: want.notify,
            },
            ...(desk ? { [DESK_PREF]: want.desk } : {}),
          },
        })
        .catch(() => undefined);
      if (want.notify) {
        // Não espera o sistema responder: em alguns aparelhos o pedido não volta.
        void requestNotifyPermission()
          .then((ok) => {
            setNotify(ok ? 'granted' : 'denied');
            if (ok && want.preview) void previewSoon();
          })
          .catch(() => setNotify('denied'));
      }
      track('feature_activated', { feature: 'boas_vindas_pronto' });
    } finally {
      setReadying(false);
    }
    if (phoneExtras) setExtras(true);
    else setScene('rosto');
  }

  const palette = paletteFor(characterKey);
  const stageStyle = {
    '--bv-light': palette.light,
    '--bv-mid': palette.mid,
    '--bv-deep': palette.deep,
  } as CSSProperties;
  const enter = reduced
    ? { initial: false as const }
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
      };

  return (
    <div className={`bv bv-${scene}`} style={stageStyle}>
      {scene !== 'rosto' && (
        <button className="bv-skip" onClick={() => void finish(false)}>
          Pular
        </button>
      )}

      {scene === 'oi' && (
        <>
          <div className="bv-stage">
            <CharacterStage
              characterKey={characterKey}
              expression="simpatico"
              mood="happy"
              name={shownName}
              say="Oi. Eu sou o seu Secretário. Eu lembro, marco e aviso."
            />
          </div>
          <motion.div className="bv-sheet" key="oi" {...enter}>
            <h1>Oi. Eu sou o seu Secretário.</h1>
            <p>Eu lembro, marco e aviso. Você só me fala.</p>
            <button className="bv-btn" onClick={() => setScene('pedido')}>
              Começar
            </button>
          </motion.div>
        </>
      )}

      {scene === 'pedido' && (
        <motion.div className="bv-page" key="pedido" {...enter}>
          <Portrait
            characterKey={characterKey}
            name={shownName}
            expression="tranquilo"
            mood={busy ? 'thinking' : error ? 'concerned' : reply ? 'curious' : 'neutral'}
            {...(!busy && reply ? { say: reply } : {})}
          />
          <h1>O que eu não posso deixar você esquecer?</h1>
          <p className="bv-lead">Fale ou escreva do seu jeito. Eu cuido do resto.</p>
          <div className="bv-chips">
            {SUGGESTIONS.map((suggestion) => (
              <button key={suggestion} type="button" onClick={() => setText(suggestion)}>
                {suggestion}
              </button>
            ))}
          </div>
          <div className="bv-talk" aria-live="polite">
            {busy && (
              <ul className="bv-steps">
                {(steps.length ? steps : ['Entendendo o que você pediu']).map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            )}
            {!busy && reply && <p className="bv-reply">{reply}</p>}
            {error && (
              <p className="bv-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <form
            className="bv-field"
            onSubmit={(event) => {
              event.preventDefault();
              void send(text);
            }}
          >
            <input
              id="bv-pedido"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Ex.: dentista quinta às 15h"
              aria-label="Seu pedido"
              maxLength={2000}
              disabled={busy}
            />
            {text.trim() ? (
              <button className="bv-round" type="submit" disabled={busy} aria-label="Enviar">
                <SendIcon />
              </button>
            ) : (
              canDictate() && (
                <button
                  className={`bv-round${listening ? ' on' : ''}`}
                  type="button"
                  onClick={() => void listen()}
                  aria-label={listening ? 'Parar de ouvir' : 'Falar o pedido'}
                >
                  <MicIcon />
                </button>
              )
            )}
          </form>
        </motion.div>
      )}

      {scene === 'feito' && done && (
        <motion.div className="bv-page bv-done" key="feito" {...enter}>
          <Portrait
            characterKey={characterKey}
            name={shownName}
            expression="divertido"
            mood="proud"
            say={done.reply || 'Pronto, anotado.'}
          />
          <div className="bv-card">
            <motion.svg
              className="bv-stamp"
              viewBox="0 0 96 96"
              aria-hidden="true"
              initial={reduced ? false : { scale: 1.7, rotate: -18, opacity: 0 }}
              animate={{ scale: 1, rotate: -8, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 520, damping: 22, mass: 0.8, delay: 0.35 }}
            >
              <circle cx="48" cy="48" r="44" className="bv-stamp-ring" />
              <circle cx="48" cy="48" r="36" className="bv-stamp-in" />
              <path d="M31 49.5 43 61l23-26" className="bv-stamp-check" />
            </motion.svg>
            <ResultCards items={[done.item]} onPick={() => true} />
          </div>
          <p className="bv-lead">
            {notify === 'granted'
              ? 'Eu aviso na hora certa, mesmo com o celular parado.'
              : 'Agora vamos deixar o resto pronto, em um toque.'}
          </p>
          <button className="bv-btn" onClick={() => setScene('pronto')}>
            Continuar
          </button>
        </motion.div>
      )}

      {scene === 'feito' && !done && (
        <motion.div className="bv-page" key="feito-vazio" {...enter}>
          <h1>Vamos ao seu primeiro pedido</h1>
          <button className="bv-btn" onClick={() => setScene('pedido')}>
            Fazer o pedido
          </button>
        </motion.div>
      )}

      {scene === 'pronto' && !extras && (
        <motion.div className="bv-page bv-ready" key="pronto" {...enter}>
          <h1>Vamos deixar tudo pronto</h1>
          <p className="bv-lead">
            Eu ligo o que faz diferença no dia a dia. Dá para mudar quando quiser, em Ajustes.
          </p>
          <ul className="bv-checks">
            {READY_ITEMS.filter((item) => item.key !== 'desk' || desk).map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={want[item.key]}
                  className={want[item.key] ? 'on' : ''}
                  onClick={() =>
                    setWant((current) => ({ ...current, [item.key]: !current[item.key] }))
                  }
                >
                  <span className="bv-check-t">
                    <b>{item.title}</b>
                    <small>{item.hint.replace('{nome}', shownName)}</small>
                  </span>
                  <i aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <button className="bv-btn" onClick={() => void makeReady()} disabled={readying}>
            {readying ? 'Um instante' : 'Ligar e continuar'}
          </button>
        </motion.div>
      )}

      {scene === 'pronto' && extras && (
        <motion.div className="bv-page bv-ready" key="pronto-extras" {...enter}>
          <h1>Mais dois atalhos do celular</h1>
          <p className="bv-lead">Cada um abre uma tela do Android: é só confirmar lá e voltar.</p>
          <ul className="bv-checks">
            {liveServiceAvailable() && (
              <li>
                <button type="button" onClick={() => void openAssistantSettings()}>
                  <span className="bv-check-t">
                    <b>Assistente do celular</b>
                    <small>Me chame segurando o botão lateral, até com a tela bloqueada</small>
                  </span>
                  <em>Escolher</em>
                </button>
              </li>
            )}
            {inboxSupported() && (
              <li>
                <button type="button" onClick={() => setInboxAsk(true)}>
                  <span className="bv-check-t">
                    <b>Mensagens de outros apps</b>
                    <small>Leio o que chega no WhatsApp e te ajudo a responder</small>
                  </span>
                  <em>Ligar</em>
                </button>
              </li>
            )}
          </ul>
          <button className="bv-btn" onClick={() => setScene('rosto')}>
            Continuar
          </button>
        </motion.div>
      )}
      {inboxAsk && (
        <InboxConsent
          onClose={() => {
            setInboxAsk(false);
            setInboxDraftWanted(true);
          }}
        />
      )}

      {scene === 'rosto' && (
        <motion.div className="bv-page bv-face" key="rosto" {...enter}>
          <Portrait characterKey={characterKey} name={shownName} mood="happy" />
          <h1>Quer me dar outro rosto?</h1>
          <p className="bv-lead">Dá para trocar quando quiser, em Mudar jeito.</p>
          <div className="bv-faces" role="radiogroup" aria-label="Rosto do Secretário">
            {FACES.map((key) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={key === characterKey}
                aria-label={characterName(key)}
                className={key === characterKey ? 'on' : ''}
                onClick={() => {
                  hapticTap();
                  setCharacterKey(key);
                }}
              >
                <img src={characterTile(key, 'simpatico')} alt="" />
              </button>
            ))}
          </div>
          <label className="bv-name" htmlFor="bv-nome">
            <span>Meu nome</span>
            <input
              id="bv-nome"
              value={shownName}
              maxLength={40}
              onChange={(event) => {
                setNameEdited(true);
                setName(event.target.value);
              }}
            />
          </label>
          <div className="bv-actions">
            <button
              className="bv-btn"
              onClick={() => void finish(true)}
              disabled={update.isPending}
            >
              {update.isPending ? 'Um instante' : 'Pronto'}
            </button>
          </div>
        </motion.div>
      )}

      {consent !== null && (
        <AiConsentSheet
          characterKey={characterKey}
          secretaryName={shownName}
          onClose={() => {
            const pending = consent;
            setConsent(null);
            // Aceitou: o pedido segue. Recusou: fica no campo, e dá para pular.
            void qc.refetchQueries({ queryKey: PROFILE_KEY }).then(() => {
              const fresh = qc.getQueryData<{ preferences?: Record<string, unknown> }>(PROFILE_KEY);
              if (hasAiConsent(fresh?.preferences)) void send(pending, true);
            });
          }}
        />
      )}
    </div>
  );
}
